import { useState, useRef } from 'react';
import { X, ChevronDown, Check } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * MultiSelect Component
 * A searchable multi-select dropdown with badge display
 */
export function MultiSelect({
  options = [],
  value = [],
  onChange,
  placeholder = 'Select options...',
  label,
  disabled = false,
  maxDisplayed = 3,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef(null);

  // Filter options based on search query
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle option selection
  const handleSelect = (optionValue) => {
    if (disabled) return;

    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];

    onChange(newValue);
  };

  // Remove a selected item
  const handleRemove = (optionValue, e) => {
    e.stopPropagation();
    if (disabled) return;
    onChange(value.filter((v) => v !== optionValue));
  };

  // Clear all selections
  const handleClear = (e) => {
    e.stopPropagation();
    if (disabled) return;
    onChange([]);
  };

  // Get label for a value
  const getLabel = (val) => {
    const option = options.find((opt) => opt.value === val);
    return option ? option.label : val;
  };

  // Get displayed badges
  const displayedValues = value.slice(0, maxDisplayed);
  const remainingCount = value.length - maxDisplayed;

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <label className="text-sm font-medium text-gray-700">{label}</label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'w-full justify-between min-h-[40px] h-auto py-2',
              value.length > 0 && 'h-auto',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <div className="flex flex-wrap gap-1 items-center">
              {value.length === 0 ? (
                <span className="text-muted-foreground">{placeholder}</span>
              ) : (
                <>
                  {displayedValues.map((val) => (
                    <Badge
                      key={val}
                      variant="secondary"
                      className="mr-1 mb-1 mt-1"
                    >
                      {getLabel(val)}
                      {!disabled && (
                        <button
                          className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={(e) => handleRemove(val, e)}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </Badge>
                  ))}
                  {remainingCount > 0 && (
                    <Badge variant="outline" className="mb-1 mt-1">
                      +{remainingCount} more
                    </Badge>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center gap-1 ml-2">
              {value.length > 0 && !disabled && (
                <button
                  className="text-muted-foreground hover:text-foreground"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleClear}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <div className="p-2">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="max-h-60 overflow-auto">
            {filteredOptions.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No options found.
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = value.includes(option.value);
                return (
                  <div
                    key={option.value}
                    className={cn(
                      'flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100',
                      isSelected && 'bg-blue-50'
                    )}
                    onClick={() => handleSelect(option.value)}
                  >
                    <div
                      className={cn(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border',
                        isSelected
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'border-gray-300'
                      )}
                    >
                      {isSelected && <Check className="h-3 w-3" />}
                    </div>
                    <span className="text-sm">{option.label}</span>
                  </div>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
      {value.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {value.length} selected
        </p>
      )}
    </div>
  );
}

export default MultiSelect;
